import { CatalogueItem } from './catalogue-category/catalogue-item.model';
import { Price } from './catalogue-category/price.model';
import { AnalyticalTest } from './analytical-test/analytical-test.model';
import { IProduct, isAnalyticalTest, isSampleService } from './contract/product.contract';
import { SampleService } from './sample-services/sample-service';
import { CartConstants } from '../constants/application-constants';
import { OrderInformation } from './order-information.model';

export const CART_ITEM_TYPE_SERVICE = 'service-cart-item';
export const CART_ITEM_TYPE_PRODUCT = 'product-cart-item';
export const CART_ITEM_TYPE_PREP_SERVICE = 'sample-service-cart-item';
export const CART_GROUP_TYPE_PRODUCT = 'product-cart-item-group';
export const CART_GROUP_TYPE_SERVICE = 'service-cart-item-group';

export class Cart {
    public groupId: number;
    constructor(public id: string,
        public quantifiedItems: CartItem[],
        public cartPrice: Price) {
    }
}

export class CartGroupRaw {
    public groupPrice: Price;
    public items: CartItem[];
    // public sampleTypeAttributeOptions: SampleTypeAttributeOption[]; //It would be good to have this value, as it
    // would help in getting the sample type more easily next time
    public resourceType: string;
    public groupNameError: CartGroupNameErrorMessage;
    public unavailableItems: UnavailableCartItem[];
    public labServices: IProduct[];

    constructor(public id: number,
        public name: string) { // here the name is the sample name if it is an AnalyticalTest
    }
}

export const deserializeCartGroupRaw = (raw: CartGroupRaw) => {
    const cloneObj = new CartGroup(raw.id, raw.name);
    // no easy way to deep clone a typed object, implementing kiss
    cloneObj.resourceType = raw.resourceType;
    cloneObj.groupPrice = !raw.groupPrice ? new Price(0, CartConstants.CURRENCY) : raw.groupPrice;
    cloneObj.groupNameError = raw.groupNameError;
    cloneObj.unavailableItems = [...raw.unavailableItems];

    cloneObj.items = raw.items.map((item: CartItem) => {
        if (isTestItem(item)) {
            return new TestItem(getItemFromRawItem(item.item), item.creationInstant, true, item.sampleTypeId, item.userAdded);
        }
        const supply = new SupplyItem(
            getItemFromRawItem(item.item),
            item.quantity,
            item.quantityPrice,
            item.creationInstant,
            item.userAdded);
        supply.autoPopulatedQty = item.autoPopulatedQty;
        return supply;
    });

    cloneObj.labServices = !raw.labServices ? [] : raw.labServices.map((item: IProduct) => {
        const labItem = getItemFromRawItem(item);
        return new SampleServiceItem(labItem, null);
    });

    return cloneObj;

};



export class CartGroup {
    public groupPrice: Price;
    public items: CartItem[];
    // public sampleTypeAttributeOptions: SampleTypeAttributeOption[]; //It would be good to have this value, as it
    // would help in getting the sample type more easily next time
    public resourceType: string;
    public groupNameError: CartGroupNameErrorMessage;
    public unavailableItems: UnavailableCartItem[];
    public labServices: CartItem[];

    constructor(public id: number,
        public name: string) { // here the name is the sample name if it is an AnalyticalTest
    }

    public clone() {
        const cloneObj = new CartGroup(this.id, this.name);
        // no easy way to deep clone a typed object, implementing kiss
        // related article: https://stackoverflow.com/questions/28150967/typescript-cloning-object
        cloneObj.resourceType = this.resourceType;
        cloneObj.groupPrice = this.groupPrice;
        cloneObj.items = [...this.items];
        cloneObj.groupNameError = this.groupNameError;
        cloneObj.unavailableItems = [...this.unavailableItems];
        cloneObj.labServices = this.labServices;

        return cloneObj;
    }

}

export class CartGroupNameErrorMessage {
    constructor(public errorMessage: string,
        public userInputGroupName: string) {}
}

export class CartSampling {
    constructor(public groupId: number,
        public name: string) {
    }
}

export class CartItem {
    sampleTypeId?: number;
    autoPopulated = false;
    autoPopulatedQty?: number;
    constructor(public item: IProduct,
        public quantity: number,
        public quantityPrice: Price,
        public creationInstant: Date, // Example Date: Mon Dec 04 2017 14:27:13 GMT+1300 (New Zealand Daylight Time)
        public resourceType = CART_ITEM_TYPE_PRODUCT,
        public itemAvailable: boolean = true,
        public userAdded: boolean = false) {
    }

    public clone() {
        const price = new Price(this.quantityPrice.amount, this.quantityPrice.currency);

        const cloneObj = new CartItem(
            this.item,
            this.quantity,
            price,
            this.creationInstant,
            this.resourceType,
            this.itemAvailable
        );
        // no easy way to deep clone a typed object, implementing kiss
        // related article: https://stackoverflow.com/questions/28150967/typescript-cloning-object
        cloneObj.sampleTypeId = this.sampleTypeId;
        cloneObj.autoPopulatedQty = this.autoPopulatedQty;
        return cloneObj;
    }

}

export class SupplyItem extends CartItem {
    constructor(public item: IProduct,
        public quantity: number,
        public quantityPrice: Price,
        public creationInstant: Date,
        public userAdded: boolean = false,
        public itemAvailable: boolean = true) {
        super(null, quantity, quantityPrice, creationInstant, CART_ITEM_TYPE_PRODUCT, userAdded, itemAvailable);
    }

    public clone() {
        const price = new Price(this.quantityPrice.amount, this.quantityPrice.currency);

        const cloneObj = new SupplyItem(
            this.item,
            this.quantity,
            price,
            this.creationInstant,
            this.userAdded,
            this.itemAvailable
        );
        // no easy way to deep clone a typed object, implementing kiss
        // related article: https://stackoverflow.com/questions/28150967/typescript-cloning-object
        cloneObj.sampleTypeId = this.sampleTypeId;
        cloneObj.autoPopulatedQty = this.autoPopulatedQty;
        return cloneObj;
    }
}

export class TestItem extends CartItem {

    constructor(
        public item: IProduct,
        public creationInstant: Date,
        public itemAvailable: boolean = true,
        public sampleTypeId: number = null,
        public userAdded: boolean = false) {
        super(null, 1, item.price, creationInstant, CART_ITEM_TYPE_SERVICE, itemAvailable, userAdded);
    }

    public clone() {
        const price = new Price(this.quantityPrice.amount, this.quantityPrice.currency);

        const cloneObj = new TestItem(
            this.item,
            this.creationInstant,
            this.itemAvailable,
            this.sampleTypeId,
            this.userAdded
        );
        // no easy way to deep clone a typed object, implementing kiss
        // related article: https://stackoverflow.com/questions/28150967/typescript-cloning-object
        cloneObj.autoPopulatedQty = this.autoPopulatedQty;
        return cloneObj;
    }

}

export class SampleServiceItem extends CartItem {
    constructor(
        public item: IProduct,
        public creationInstant: Date,
        public itemAvailable: boolean = true) {
        super(null, 1, item.price, creationInstant, CART_ITEM_TYPE_PREP_SERVICE, itemAvailable);
    }

    public clone() {
        const price = new Price(this.quantityPrice.amount, this.quantityPrice.currency);

        const cloneObj = new SampleServiceItem(
            this.item,
            this.creationInstant,
            this.itemAvailable
        );
        // no easy way to deep clone a typed object, implementing kiss
        // related article: https://stackoverflow.com/questions/28150967/typescript-cloning-object
        cloneObj.autoPopulatedQty = this.autoPopulatedQty;
        return cloneObj;
    }
}


export class CartNode {
    public groups: CartGroupRaw[];
    constructor(public id: string,
        public cartPrice: Price) {
    }
}

/* rawString format is '[product/service]-cart-item-group.v1';*/
export const isProductGroup = (rawString: string) => rawString.search('product-cart-item-group') >= 0;

export class CartParams {
    constructor(public analyticalTest: AnalyticalTest,
        public groups: any[]) {}
}

export class UnavailableCartItem {
    constructor(public id: number,
        public name: string) {}
}

export class PlaceOrderRequest {
    constructor(
        public orderHeader: OrderInformation,
        public autoPopulatedItems: AutoPopulatedItem[] = [],
        public samplingSupplyGroupId: number | null) {}
}

export class AutoPopulatedItem {
    itemId: number;
    autoPopulatedQty: number;
}

export const isSupplyItem = (rawCartItem) => rawCartItem.resourceType.search(CART_ITEM_TYPE_PRODUCT) >= 0;
export const isTestItem = (rawCartItem) => rawCartItem.resourceType.search(CART_ITEM_TYPE_SERVICE) >= 0;
export const isSampleServiceItem = (rawCartItem) => rawCartItem.resourceType.search(CART_ITEM_TYPE_PREP_SERVICE) >= 0;

export const getGATrackingItem = (cartItem: CartItem|TestItem|SupplyItem) => {
    const result =  {
        name: cartItem.item.name,
        price: cartItem.item.price.amount,
        quantity: cartItem.quantity
    } as any;

    if (cartItem.item instanceof AnalyticalTest) {
        result.sku = cartItem.item.code;
        result.category = 'Test';
    }

    if (cartItem.item instanceof CatalogueItem) {
        result.sku = 'Supply-Item-' + cartItem.item.id;
        result.category = 'Sampling Supplies';
        if (cartItem.quantity === 0) {
            result.quantity = cartItem.autoPopulatedQty;
        }
    }

    return result;
};

export const getItemFromRawItem = (rawProduct) => {

    let typedItem: IProduct;

    if (isAnalyticalTest(rawProduct)) {
        typedItem = new AnalyticalTest(
            rawProduct.id,
            rawProduct.name,
            rawProduct.description,
            rawProduct.price,
            rawProduct.turnAroundDuration,
            rawProduct.code
        );

        const info = !!rawProduct['extendedInfo']
            ? rawProduct['extendedInfo']
            : {};
        const { temperatureRequirement, timeRequirement, measurands } = info;
        (<AnalyticalTest>typedItem).assignExtendedInfo(temperatureRequirement, timeRequirement, measurands);

    } else if (isSampleService(rawProduct)) {
        typedItem = new SampleService(
            rawProduct.id,
            rawProduct.name,
            rawProduct.price,
            rawProduct.sampleTypeId
        );
    } else { // Default
        typedItem = new CatalogueItem(
            rawProduct.id,
            rawProduct.name,
            rawProduct.code,
            rawProduct.description,
            rawProduct.quantity,
            rawProduct.price,
            rawProduct.categories,
            rawProduct.categoryId,
            rawProduct.resourceType
        );
    }

    return typedItem;

};
